import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Formik } from "formik";
import type { FormikErrors, FormikHandlers, FormikTouched } from "formik";
import { Form } from "react-bootstrap";
import MaskedInput from "components/MaskedInput";
import moment from "moment";

import {
  address2Validator,
  DOB_FORMAT_ERROR,
  IndividualAddress1Validator,
  INVALID_SSN_ERROR,
  parseDob,
  postalCodeValidator,
  stateValidator,
  testDobFormat,
  testSsn,
  validateUsersAddress,
} from "utils/fieldValidators";
import { userService } from "services/userService";
import SimpleModal from "components/SimpleModal";
import AddressValidator from "components/AddressValidator";
import Button from "components/Button";

import AddressForm from "./forms/AddressForm";

import * as yup from "yup";

const createSsnSchema = () =>
  yup.object({
    ssn: yup
      .string()
      .trim()
      .required()
      .label("Social Security Number")
      // found-not-fixed: testSsn passed directly to .test() hands yup's
      // TestContext to the truthy allowNoDash parameter, silently loosening the
      // SSN format check (the documented allowNoDash footgun). Preserved verbatim.
      // testSsn's (value, allowNoDash?: boolean) signature is structurally
      // disjoint from yup's TestFunction (value, TestContext); boundary-cast so
      // the same function reference is passed to .test() exactly as master did.
      .test(
        "ssn",
        INVALID_SSN_ERROR,
        testSsn as unknown as yup.TestFunction<string>
      ),

    confirmSsn: yup
      .string()
      .trim()
      .required()
      .label("Confirm Social Security Number"),
  });

const createDobSchema = () =>
  yup.object({
    dob: yup
      .string()
      .required()
      .label("Date of Birth")
      .test("dob-format", DOB_FORMAT_ERROR, testDobFormat)
      .test("dob", "You must be at least 18", (value) => {
        return (
          parseDob(value).isValid() &&
          moment().diff(parseDob(value), "years") >= 18
        );
      }),

    confirmDob: yup.string().trim().required().label("Confirm Date of Birth"),
  });

const createAddressSchema = () =>
  yup.object({
    address1: IndividualAddress1Validator,
    address2: address2Validator,
    city: yup.string().label("City").required().max(40),
    state: stateValidator,
    postalCode: postalCodeValidator,
  });

type FieldType = "ssn" | "dob" | "address";

const FIELD_CONFIG: Record<FieldType, { title: string }> = {
  ssn: { title: "SSN" },
  dob: { title: "Date of Birth" },
  address: { title: "Address" },
};

interface MemberAddress {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

/**
 * Single union shape covering all three field modes. _getInitialValues seeds
 * the address fields for fieldType==="address" and the ssn/dob fields
 * otherwise; the render-prop helpers only read the fields relevant to the
 * active mode, so unused fields are optional.
 */
interface FormValues {
  ssn?: string;
  confirmSsn?: string;
  dob?: string;
  confirmDob?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

/** The two payload shapes this.props.onSubmit is invoked with. */
type SubmitPayload =
  | { memberId: string; fieldType: "ssn" | "dob"; value: string }
  | { memberId: string; fieldType: "address"; address: MemberAddress };

interface Props {
  show: boolean;
  onClose: () => void;
  onSubmit: (payload: SubmitPayload) => void;
  memberName: string;
  memberId: string;
  fieldType: FieldType;
  // currentSsn/currentDob are declared by the original propTypes but never read
  // in the body; kept optional to mirror the prop surface.
  currentSsn?: string;
  currentDob?: string;
  currentAddress?: MemberAddress;
  // injected by the parent (not via withApollo here); forwarded to
  // userService.getValidatedAddress.
  client?: ApolloClient<object>;
  isSubmitting?: boolean;
}

interface State {
  showAddressModal: boolean;
  fetchingAddress: boolean;
  validatedAddress: MemberAddress;
  editedValues: FormValues | null;
}

/**
 * The Formik render-prop subset forwarded into the per-mode field renderers.
 * handleChange is Formik's own handler; MaskedInput's onAccept feeds it a
 * synthetic { target: { name, value } } cast to a ChangeEvent (as in the
 * canonical Beneficiaries form).
 */
interface FieldRenderProps {
  handleChange: FormikHandlers["handleChange"];
  handleBlur: FormikHandlers["handleBlur"];
  values: FormValues;
  touched: FormikTouched<FormValues>;
  errors: FormikErrors<FormValues>;
}

/** Shape MaskedInput's onAccept hands back as its second arg. */
interface MaskRef {
  el: { input: { name: string } };
}

/** Subset of Formik helpers stashed for the AddressValidator modal. */
interface FormikHelpersRef {
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  values: FormValues;
}

class MemberFieldEditModal extends React.PureComponent<Props, State> {
  state: State = {
    showAddressModal: false,
    fetchingAddress: false,
    validatedAddress: {},
    editedValues: null,
  };

  _formikHelpers: FormikHelpersRef | null = null;

  _getSchema = () => {
    const { fieldType } = this.props;

    if (fieldType === "ssn") {
      return createSsnSchema();
    }
    if (fieldType === "dob") {
      return createDobSchema();
    }
    return createAddressSchema();
  };

  _getInitialValues = (): FormValues => {
    const { fieldType, currentAddress } = this.props;

    if (fieldType === "address") {
      return (
        this.state.editedValues || {
          address1: currentAddress?.address1 || "",
          address2: currentAddress?.address2 || "",
          city: currentAddress?.city || "",
          state: currentAddress?.state || "",
          postalCode: currentAddress?.postalCode || "",
        }
      );
    }
    return {
      ssn: "",
      confirmSsn: "",
      dob: "",
      confirmDob: "",
    };
  };

  _handleSubmit = async (values: FormValues, skipAddressValidation = false) => {
    const { fieldType, memberId } = this.props;

    if (fieldType === "address") {
      await this._handleAddressSubmit(values, skipAddressValidation);
    } else {
      const value =
        fieldType === "ssn"
          ? values.ssn
          : parseDob(values.dob).format("YYYY/MM/DD");

      this.props.onSubmit({
        memberId,
        fieldType,
        // fieldType is narrowed to "ssn" | "dob" here; for "ssn" `value` is
        // values.ssn (string | undefined). Coerced to satisfy the payload's
        // string `value`; runtime unchanged (yup .required() guards submit).
        value: value as string,
      });
    }
  };

  _handleAddressSubmit = async (
    values: FormValues,
    skipAddressValidation = false
  ) => {
    if (!skipAddressValidation) {
      try {
        this.setState({ fetchingAddress: true });

        const addressToValidate = {
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          state: values.state,
          postalCode: values.postalCode,
        };

        const validatedAddress = await userService.getValidatedAddress(
          // client is injected by the parent; cast as the other action/service
          // call sites in this repo do.
          this.props.client as ApolloClient<object>,
          // addressToValidate fields are optional here but the query variables
          // type marks address1/city/state/postalCode non-null; runtime values
          // are the form's (yup-required) strings.
          addressToValidate as {
            address1: string;
            address2?: string;
            city: string;
            state: string;
            postalCode: string;
          }
        );

        this.setState({ fetchingAddress: false });

        if (validatedAddress) {
          const userAddressIsValid = validateUsersAddress(
            addressToValidate,
            // the GraphQL result includes __typename/possibly-null fields;
            // validateUsersAddress only reads the address-like keys.
            validatedAddress as MemberAddress
          );

          if (!userAddressIsValid) {
            this.setState({
              showAddressModal: true,
              validatedAddress: validatedAddress as MemberAddress,
              editedValues: { ...values },
            });
            return;
          }
        }
      } catch (error) {
        console.log(error);
        this.setState({ fetchingAddress: false });
      }
    }

    this.props.onSubmit({
      memberId: this.props.memberId,
      fieldType: "address",
      address: {
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
      },
    });
  };

  _closeAddressModal = () => {
    this.setState({ showAddressModal: false });
  };

  _renderSsnFields = ({
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
  }: FieldRenderProps) => {
    return (
      <>
        <Form.Group className="mb-3" controlId="formSsnField">
          <Form.Label>New Social Security Number</Form.Label>
          <Form.Control
            as={MaskedInput}
            mask="000-00-0000"
            name="ssn"
            placeholder="000-00-0000"
            value={values.ssn}
            onAccept={(val: unknown, mask: unknown) =>
              handleChange({
                target: {
                  name: (mask as MaskRef).el.input.name,
                  value: val,
                },
                // synthetic event Formik's handleChange reads name/value off of
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            onBlur={handleBlur}
            isInvalid={touched.ssn && !!errors.ssn}
            isValid={touched.ssn && !errors.ssn}
          />
          <Form.Control.Feedback type="invalid">
            {errors.ssn}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formConfirmSsnField">
          <Form.Label>Confirm Social Security Number</Form.Label>
          <Form.Control
            as={MaskedInput}
            mask="000-00-0000"
            name="confirmSsn"
            placeholder="000-00-0000"
            value={values.confirmSsn}
            onAccept={(val: unknown, mask: unknown) =>
              handleChange({
                target: {
                  name: (mask as MaskRef).el.input.name,
                  value: val,
                },
                // synthetic event Formik's handleChange reads name/value off of
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            onBlur={handleBlur}
            isInvalid={touched.confirmSsn && !!errors.confirmSsn}
            isValid={touched.confirmSsn && !errors.confirmSsn}
          />
          <Form.Control.Feedback type="invalid">
            {errors.confirmSsn}
          </Form.Control.Feedback>
        </Form.Group>
      </>
    );
  };

  _renderDobFields = ({
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
  }: FieldRenderProps) => {
    return (
      <>
        <Form.Group className="mb-3" controlId="formDobField">
          <Form.Label>New Date of Birth</Form.Label>
          <Form.Control
            as={MaskedInput}
            mask="00/00/0000"
            name="dob"
            placeholder="MM/DD/YYYY"
            value={values.dob}
            onAccept={(val: unknown, mask: unknown) =>
              handleChange({
                target: {
                  name: (mask as MaskRef).el.input.name,
                  value: val,
                },
                // synthetic event Formik's handleChange reads name/value off of
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            onBlur={handleBlur}
            isInvalid={touched.dob && !!errors.dob}
            isValid={touched.dob && !errors.dob}
          />
          <Form.Control.Feedback type="invalid">
            {errors.dob}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formConfirmDobField">
          <Form.Label>Confirm Date of Birth</Form.Label>
          <Form.Control
            as={MaskedInput}
            mask="00/00/0000"
            name="confirmDob"
            placeholder="MM/DD/YYYY"
            value={values.confirmDob}
            onAccept={(val: unknown, mask: unknown) =>
              handleChange({
                target: {
                  name: (mask as MaskRef).el.input.name,
                  value: val,
                },
                // synthetic event Formik's handleChange reads name/value off of
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            onBlur={handleBlur}
            isInvalid={touched.confirmDob && !!errors.confirmDob}
            isValid={touched.confirmDob && !errors.confirmDob}
          />
          <Form.Control.Feedback type="invalid">
            {errors.confirmDob}
          </Form.Control.Feedback>
        </Form.Group>
      </>
    );
  };

  _renderFields = (formikProps: FieldRenderProps) => {
    const { fieldType } = this.props;

    if (fieldType === "ssn") {
      return this._renderSsnFields(formikProps);
    }
    if (fieldType === "dob") {
      return this._renderDobFields(formikProps);
    }
    // AddressForm narrows handleChange to ChangeEventHandler<HTMLElement>;
    // FieldRenderProps.handleChange is the broader Formik shape. Forwarded as
    // in master; cast at the boundary rather than widening AddressForm.
    return (
      <AddressForm
        {...(formikProps as unknown as React.ComponentProps<
          typeof AddressForm
        >)}
      />
    );
  };

  render() {
    const { show, onClose, memberName, fieldType, isSubmitting } = this.props;
    const { title } = FIELD_CONFIG[fieldType];

    return (
      <>
        {this.state.showAddressModal && (
          <AddressValidator
            show={this.state.showAddressModal}
            onSubmit={this._handleSubmit}
            setFieldValue={this._formikHelpers?.setFieldValue}
            // validatedAddress is MemberAddress (no index signature); the prop
            // type carries [key: string]: unknown. Same runtime object
            // forwarded as master; boundary-cast for the structural shape.
            validatedAddress={
              this.state.validatedAddress as React.ComponentProps<
                typeof AddressValidator
              >["validatedAddress"]
            }
            onClose={this._closeAddressModal}
            // master forwards possibly-undefined _formikHelpers.values to a
            // required prop; AddressValidator only reads it once the address
            // modal is shown (helpers are set in the address render path).
            // @ts-expect-error -- FormValues | undefined vs required AddressValidatorAddress; preserved from master
            values={this._formikHelpers?.values}
            allowPoBox={true}
          />
        )}
        <SimpleModal
          show={show && !this.state.showAddressModal}
          onClose={onClose}
          title={`Edit ${title} for ${memberName}`}
          buttonText="Submit"
          hideActions
          showCloseButton
        >
          <Formik
            validateOnChange={false}
            validationSchema={this._getSchema()}
            onSubmit={(values) => this._handleSubmit(values)}
            initialValues={this._getInitialValues()}
            enableReinitialize={true}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              setFieldValue,
            }) => {
              if (fieldType === "address") {
                this._formikHelpers = { setFieldValue, values };
              }
              return (
                <Form noValidate onSubmit={handleSubmit}>
                  {this._renderFields({
                    handleChange,
                    handleBlur,
                    values,
                    touched,
                    errors,
                  })}
                  <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                    <Button
                      btnLabel="Cancel"
                      name="cancel"
                      type="button"
                      color="cancel"
                      onClick={onClose}
                    />
                    <Button
                      btnLabel="Submit for Approval"
                      name="submit"
                      type="submit"
                      loading={isSubmitting || this.state.fetchingAddress}
                      disabled={isSubmitting || this.state.fetchingAddress}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </SimpleModal>
      </>
    );
  }
}

export default MemberFieldEditModal;
